import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import './Analytics.css';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun'];

function getRealAnalyticsData() {
  let leads = [], clients = [], invoices = [];
  try { leads = JSON.parse(localStorage.getItem('ryzenos_leads')||'[]'); } catch {}
  try { clients = JSON.parse(localStorage.getItem('ryzenos_clients')||'[]'); } catch {}
  try { invoices = JSON.parse(localStorage.getItem('ryzenos_invoices')||'[]'); } catch {}

  // Revenue last 6 months
  const now = new Date();
  const revenue = Array.from({length:6}).map((_,idx) => {
    const m = (now.getMonth() - 5 + idx + 12) % 12;
    const y = now.getMonth() - 5 + idx < 0 ? now.getFullYear() - 1 : now.getFullYear();
    return invoices.filter(i => {
      if (i.status !== 'paid' || !i.date) return false;
      const d = new Date(i.date);
      return d.getMonth() === m && d.getFullYear() === y;
    }).reduce((s,i)=>s+(i.amount||0),0);
  });

  // Lead sources
  const sourceCounts = {};
  leads.forEach(l => { const s = l.source || 'Other'; sourceCounts[s] = (sourceCounts[s]||0)+1; });
  const totalLeads = leads.length || 1;
  const colors = ['#F5A623','#3B82F6','#22C55E','#8B5CF6','#EF4444'];
  const leadSources = Object.entries(sourceCounts).map(([label,count],idx) => ({
    label, value: Math.round((count/totalLeads)*100), color: colors[idx % colors.length]
  })).sort((a,b)=>b.value-a.value);

  // Pipeline by status
  const stageLabels = [
    {id:'new',label:'New',color:'#22C55E'},{id:'contacted',label:'Contacted',color:'#3B82F6'},
    {id:'meeting-done',label:'Meeting Done',color:'#60A5FA'},{id:'proposal-sent',label:'Proposal Sent',color:'#F5A623'},
    {id:'negotiation',label:'Negotiation',color:'#F5A623'},{id:'won',label:'Won',color:'#22C55E'},
    {id:'lost',label:'Lost',color:'#EF4444'},
  ];
  const pipeline = stageLabels.map(s => ({ label:s.label, value: leads.filter(l=>l.status===s.id).length, color:s.color }));

  // Top clients by invoiced value
  const clientTotals = {};
  invoices.forEach(i => { if (i.client) clientTotals[i.client] = (clientTotals[i.client]||0) + (i.amount||0); });
  const topClients = Object.entries(clientTotals)
    .map(([name,value]) => {
      const c = clients.find(c => c.name === name);
      return { name, value, city: c?.location || '' };
    })
    .sort((a,b)=>b.value-a.value).slice(0,5);

  // Client locations
  const locationCounts = {};
  clients.forEach(c => { if (c.location) locationCounts[c.location] = (locationCounts[c.location]||0)+1; });
  const clientLocations = Object.entries(locationCounts).map(([city,clients])=>({city,clients}));

  return { revenue, leadSources, pipeline, topClients, clientLocations };
}

const fmtINR = v => '₹' + v.toLocaleString('en-IN');

/* ── Dot World Map ── */
function DotWorldMap() {
  const W = 580, H = 260;
  /* Equirectangular projection -- full world */
  const toXY = (lat, lng) => ({
    x: ((lng + 180) / 360) * W,
    y: ((90 - lat) / 180) * H,
  });

  /* Land mass dot grids */
  const landDots = [];

  const addGrid = (latMin, latMax, lngMin, lngMax, step, check) => {
    for (let lat = latMin; lat <= latMax; lat += step) {
      for (let lng = lngMin; lng <= lngMax; lng += step) {
        if (!check || check(lat, lng)) landDots.push({lat, lng});
      }
    }
  };

  /* India */
  addGrid(8, 37, 68, 97, 1.4, (la, ln) =>
    (la>8&&la<15&&ln>76&&ln<80) ||
    (la>8&&la<23&&ln>68&&ln<80) ||
    (la>15&&la<23&&ln>72&&ln<92) ||
    (la>23&&la<30&&ln>70&&ln<88) ||
    (la>30&&la<37&&ln>73&&ln<80)
  );
  /* Europe */
  addGrid(36, 70, -10, 40, 2, (la, ln) =>
    (la>36&&la<48&&ln>-5&&ln<28) ||
    (la>48&&la<58&&ln>-5&&ln<22) ||
    (la>58&&la<70&&ln>5&&ln<30) ||
    (la>44&&la<52&&ln>14&&ln<40)
  );
  /* Africa */
  addGrid(-35, 37, -18, 52, 2, (la, ln) =>
    (la>-35&&la<-20&&ln>16&&ln<36) ||
    (la>-20&&la<5&&ln>10&&ln<42) ||
    (la>5&&la<15&&ln>-15&&ln<42) ||
    (la>15&&la<37&&ln>-5&&ln<36)
  );
  /* North America */
  addGrid(15, 72, -168, -52, 2, (la, ln) =>
    (la>15&&la<30&&ln>-118&&ln<-86) ||
    (la>30&&la<50&&ln>-125&&ln<-70) ||
    (la>50&&la<72&&ln>-140&&ln<-60)
  );
  /* South America */
  addGrid(-56, 12, -82, -34, 2, (la, ln) =>
    (la>-10&&la<12&&ln>-82&&ln<-46) ||
    (la>-30&&la<-10&&ln>-75&&ln<-34) ||
    (la>-56&&la<-30&&ln>-76&&ln<-48)
  );
  /* Asia (ex India) */
  addGrid(0, 55, 40, 145, 2, (la, ln) =>
    (la>35&&la<55&&ln>40&&ln<135) ||
    (la>20&&la<35&&ln>45&&ln<122) ||
    (la>0&&la<20&&ln>95&&ln<122) ||
    (la>20&&la<40&&ln>100&&ln<122)
  );
  /* Australia */
  addGrid(-44, -10, 113, 154, 2, (la, ln) =>
    (la>-44&&la<-10&&ln>113&&ln<154)
  );
  /* Arabian Peninsula */
  addGrid(12, 30, 36, 60, 1.8, (la, ln) =>
    (la>12&&la<26&&ln>36&&ln<60)
  );
  /* SE Asia */
  addGrid(0, 22, 95, 125, 1.8);
  /* Pakistan/Afghanistan */
  addGrid(24, 38, 60, 74, 1.6);
  /* Japan/Korea */
  addGrid(30, 46, 128, 146, 1.8);

  return (
    <div className="an-map-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="an-map-svg" preserveAspectRatio="xMidYMid meet">
        {/* Land dots */}
        {landDots.map((d,i) => {
          const {x,y} = toXY(d.lat, d.lng);
          return <circle key={i} cx={x} cy={y} r="1.1" fill="currentColor" opacity="0.15"/>;
        })}
        {/* Client dots */}
        {CLIENT_LOCATIONS.map((loc,i) => {
          const {x,y} = toXY(loc.lat, loc.lng);
          const r = 3 + loc.clients * 1.2;
          return (
            <g key={i}>
              <circle cx={x} cy={y} r={r*3} fill="#F5A623" opacity="0.08"/>
              <circle cx={x} cy={y} r={r*1.8} fill="#F5A623" opacity="0.15"/>
              <circle cx={x} cy={y} r={r} fill="#F5A623" opacity="1"/>
              <circle cx={x} cy={y} r={r*0.4} fill="#fff" opacity="0.9"/>
              <text x={x} y={y-r-3} textAnchor="middle" fontSize="7" fill="currentColor" opacity="0.6">{loc.city}</text>
            </g>
          );
        })}
      </svg>
      <div className="an-map-legend">
        {CLIENT_LOCATIONS.map(loc=>(
          <div key={loc.city} className="an-map-leg-item">
            <div className="an-map-leg-dot"/>
            <span className="an-map-leg-city">{loc.city}</span>
            <span className="an-map-leg-count">{loc.clients}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Revenue Chart ── */
function RevenueBarChart({ revenue }) {
  const thisMonth = revenue;
  const lastMonth = revenue.map(v=>0); // no historical "last month" series without deeper data; show single series
  const fmtK = v => '₹' + v.toLocaleString('en-IN');
  const max = Math.max(1, ...thisMonth);
  const W=520,H=150,PB=24,PT=14,PL=6,PR=6;
  const cH=H-PB-PT;
  const groupW=(W-PL-PR)/MONTHS.length;
  const barW=Math.max(8,Math.floor(groupW*0.4));
  const gap=3;
  const hasData = thisMonth.some(v=>v>0);

  return (
    <div className="an-widget">
      <div className="an-widget-hd">
        <span className="an-widget-title">Revenue — last 6 months</span>
      </div>
      {!hasData ? <div style={{padding:'30px 0',textAlign:'center',fontSize:12,opacity:.4}}>No revenue recorded yet</div> : (
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height:H,display:'block',overflow:'visible'}}>
        {[0,1,2,3].map(i=>{
          const y=PT+(i*(cH/3));
          return <line key={i} x1={PL} y1={y} x2={W-PR} y2={y} stroke="currentColor" strokeOpacity="0.06" strokeWidth="0.5"/>;
        })}
        {MONTHS.map((m,i)=>{
          const cx = PL + i*groupW + groupW/2;
          const h2 = Math.max(3,(thisMonth[i]/max)*cH);
          return (
            <g key={m}>
              <rect x={cx-barW/2} y={PT+cH-h2} width={barW} height={h2} rx="3" fill="#F5A623" opacity="0.85"/>
              <text x={cx} y={H-6} textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.4">{m}</text>
              {thisMonth[i]>0 && <text x={cx} y={PT+cH-h2-6} textAnchor="middle" fontSize="8" fill="#F5A623" opacity="0.7">{fmtK(thisMonth[i])}</text>}
            </g>
          );
        })}
      </svg>
      )}
    </div>
  );
}

/* ── Pie Chart ── */
function PieChart({ leadSources }) {
  if (!leadSources || leadSources.length === 0) {
    return (
      <div className="an-widget">
        <div className="an-widget-hd"><span className="an-widget-title">Lead sources</span></div>
        <div style={{padding:'30px 0',textAlign:'center',fontSize:12,opacity:.4}}>No leads yet</div>
      </div>
    );
  }
  const total = leadSources.reduce((s,d)=>s+d.value,0) || 1;
  let angle=-90;
  const R=58,CX=80,CY=72;
  const slices = leadSources.map(d=>{
    const sweep=(d.value/total)*360;
    const sA=angle*Math.PI/180, eA=(angle+sweep)*Math.PI/180;
    const laf=sweep>180?1:0;
    const path=`M${CX},${CY} L${CX+R*Math.cos(sA)},${CY+R*Math.sin(sA)} A${R},${R} 0 ${laf},1 ${CX+R*Math.cos(eA)},${CY+R*Math.sin(eA)} Z`;
    angle+=sweep; return {...d,path};
  });
  return (
    <div className="an-widget">
      <div className="an-widget-hd"><span className="an-widget-title">Lead sources</span></div>
      <div className="an-pie-wrap">
        <svg viewBox="0 0 160 145" className="an-pie-svg">
          {slices.map(s=><path key={s.label} d={s.path} fill={s.color} opacity="0.88"/>)}
          <circle cx={CX} cy={CY} r="26" className="an-pie-hole"/>
          <text x={CX} y={CY+1} textAnchor="middle" dominantBaseline="middle" fontSize="9" fill="currentColor" opacity="0.5">Leads</text>
        </svg>
        <div className="an-pie-legend">{leadSources.map(s=>(
          <div key={s.label} className="an-pie-leg-item">
            <div className="an-pie-leg-dot" style={{background:s.color}}/>
            <span className="an-pie-leg-lbl">{s.label}</span>
            <span className="an-pie-leg-val">{s.value}%</span>
          </div>
        ))}</div>
      </div>
    </div>
  );
}

/* ── Pipeline ── */
function PipelineChart({ pipeline }) {
  const max = Math.max(1, ...pipeline.map(p=>p.value));
  return (
    <div className="an-widget an-widget-full">
      <div className="an-widget-hd"><span className="an-widget-title">Lead pipeline</span><span className="an-widget-tag">By status</span></div>
      <div className="an-pipeline-grid">
        {pipeline.map(p=>(
          <div key={p.label} className="an-pipe-item">
            <div className="an-pipe-top">
              <span className="an-pipe-lbl">{p.label}</span>
              <span className="an-pipe-val" style={{color:p.color}}>{p.value}</span>
            </div>
            <div className="an-bar-track"><div className="an-bar-fill" style={{width:`${(p.value/max)*100}%`,background:p.color}}/></div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Top Clients ── */
function TopClients({ topClients }) {
  if (!topClients || topClients.length === 0) {
    return (
      <div className="an-widget">
        <div className="an-widget-hd"><span className="an-widget-title">Top clients</span></div>
        <div style={{padding:'20px 0',textAlign:'center',fontSize:12,opacity:.4}}>No invoiced clients yet</div>
      </div>
    );
  }
  const max=Math.max(1, ...topClients.map(c=>c.value));
  return (
    <div className="an-widget">
      <div className="an-widget-hd"><span className="an-widget-title">Top clients</span></div>
      <div className="an-clients-list">{topClients.map((c,i)=>(
        <div key={c.name} className="an-client-item">
          <div className="an-client-rank">#{i+1}</div>
          <div className="an-client-info"><div className="an-client-name">{c.name}</div><div className="an-client-city">{c.city}</div></div>
          <div className="an-client-bar-wrap"><div className="an-client-bar" style={{width:`${(c.value/max)*100}%`}}/></div>
          <div className="an-client-val">{fmtINR(c.value)}</div>
        </div>
      ))}</div>
    </div>
  );
}

/* ── Main ── */
export default function Analytics() {
  const { theme } = useTheme();
  const data = getRealAnalyticsData();

  const allTeam = (() => { try { return JSON.parse(localStorage.getItem('ryzenos_team')||'[]'); } catch { return []; } })();
  const allProjects = (() => { try { return JSON.parse(localStorage.getItem('ryzenos_projects')||'[]'); } catch { return []; } })();
  const allLeads = (() => { try { return JSON.parse(localStorage.getItem('ryzenos_leads')||'[]'); } catch { return []; } })();

  const revenueThisMonth = data.revenue[data.revenue.length-1] || 0;
  const presentCount = allTeam.filter(m=>m.status==='present').length;
  const totalTeam = allTeam.length;
  const doneTasks = allProjects.filter(p=>p.status==='done').length;
  const taskPct = allProjects.length ? Math.round((doneTasks/allProjects.length)*100) : 0;
  const overdueTasks = allProjects.filter(p => p.due && p.due < new Date().toISOString().split('T')[0] && p.status !== 'done').length;
  const newLeadsCount = allLeads.filter(l=>l.status==='new').length;

  return (
    <div className="an-wrap" data-theme={theme}>
      <Sidebar/>
      <div className="an-main">
        <Topbar title="Analytics" subtitle="Business overview"/>

        {/* KPI row */}
        <div className="an-kpi-row">
          {[
            {label:'Revenue this month',val:fmtINR(revenueThisMonth),chg:'',color:'#F5A623'},
            {label:'Active leads',val:String(allLeads.length),chg:newLeadsCount+' new',color:'#22C55E'},
            {label:'Tasks done',val:allProjects.length?taskPct+'%':'—',chg:overdueTasks+' overdue',color:'#3B82F6'},
            {label:'Team present',val:totalTeam?presentCount+'/'+totalTeam:'—',chg:'',color:'#8B5CF6'},
          ].map(s=>(
            <div key={s.label} className="an-kpi-card">
              <div className="an-kpi-lbl">{s.label}</div>
              <div className="an-kpi-val" style={{color:s.color}}>{s.val}</div>
              {s.chg && <div className="an-kpi-chg">{s.chg}</div>}
            </div>
          ))}
        </div>

        <div className="an-content">
          {/* Row 1: Revenue + Pie */}
          <div className="an-row-2">
            <RevenueBarChart revenue={data.revenue}/>
            <PieChart leadSources={data.leadSources}/>
          </div>

          {/* Row 2: Pipeline full width */}
          <PipelineChart pipeline={data.pipeline}/>

          {/* Row 3: Top clients + Client locations */}
          <div className="an-row-2">
            <TopClients topClients={data.topClients}/>
            <div className="an-widget">
              <div className="an-widget-hd">
                <span className="an-widget-title">Client locations</span>
                <span className="an-widget-tag">{data.clientLocations.reduce((s,c)=>s+c.clients,0)} clients · {data.clientLocations.length} cities</span>
              </div>
              {data.clientLocations.length === 0 ? (
                <div style={{padding:'20px 0',textAlign:'center',fontSize:12,opacity:.4}}>No client locations recorded yet</div>
              ) : (
                <div className="an-pie-legend">
                  {data.clientLocations.map(loc=>(
                    <div key={loc.city} className="an-pie-leg-item">
                      <div className="an-pie-leg-dot" style={{background:'#F5A623'}}/>
                      <span className="an-pie-leg-lbl">{loc.city}</span>
                      <span className="an-pie-leg-val">{loc.clients}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
